import { create } from 'zustand';
import { ListHistories, CreateHistory, GetHistory, UpdateHistory, DeleteHistory } from '../api/api';
import toast from 'react-hot-toast';

export interface History {
  _id: string;
  period: string;
  priest: string;
  description?: string;
  image?: string;
  order: number;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
}

interface CreateHistoryI {
  period: string;
  priest: string;
  description?: string;
  image?: string;
  order?: number;
}

interface UpdateHistoryI {
  period?: string;
  priest?: string;
  description?: string;
  image?: string;
  order?: number;
}

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

interface GetHistoriesResponse {
  data: {
    histories: History[];
    pagination?: PaginationInfo;
  };
}

interface HistoryResponse {
  data: {
    history: History;
  };
  status: boolean;
  message: string;
}

interface DeleteHistoryResponse {
  status: boolean;
  message: string;
}

interface HistoryStore {
  histories: History[];
  loading: boolean;
  error: string | null;
  pagination: PaginationInfo | null;
  getHistories: (isPublic?: boolean, search?: string, page?: number, limit?: number) => Promise<void>;
  createHistory: (history: CreateHistoryI) => Promise<void>;
  getHistory: (id: string) => Promise<History | null>;
  updateHistory: (id: string, history: UpdateHistoryI) => Promise<void>;
  deleteHistory: (id: string) => Promise<void>;
}

export const useHistoryStore = create<HistoryStore>((set, get) => ({
  histories: [] as History[],
  loading: false,
  error: null,
  pagination: null,

  getHistories: async (isPublic?: boolean, search?: string, page?: number, limit?: number) => {
    set({ loading: true, error: null });
    const { data, error } = await ListHistories<GetHistoriesResponse>(isPublic, search, page, limit);

    if (error) {
      set({ loading: false, error: error as string });
      return;
    }

    set({
      histories: data.data.histories || [],
      pagination: data.data.pagination || null,
      loading: false,
    });
  },

  createHistory: async (history: CreateHistoryI) => {
    set({ loading: true, error: null });
    const { data, error } = await CreateHistory<HistoryResponse>(history);

    if (error || !data.status) {
      set({ loading: false, error: (error as string) || 'Failed to create history' });
      return;
    }

    const histories = get().histories;
    set({ histories: [...histories, data.data.history], loading: false });
    toast.success('History created successfully');
    // Refresh the list with current pagination
    const currentPagination = get().pagination;
    get().getHistories(undefined, undefined, currentPagination?.page, currentPagination?.limit);
  },

  getHistory: async (id: string) => {
    set({ loading: true, error: null });
    const { data, error } = await GetHistory<HistoryResponse>(id);

    if (error || !data.status) {
      set({ loading: false, error: (error as string) || 'Failed to get history' });
      return null;
    }

    set({ loading: false });
    return data.data.history;
  },

  updateHistory: async (id: string, history: UpdateHistoryI) => {
    set({ loading: true, error: null });
    const { data, error } = await UpdateHistory<HistoryResponse>(id, history);

    if (error || !data.status) {
      set({ loading: false, error: (error as string) || 'Failed to update history' });
      return;
    }

    const histories = get().histories;
    const updatedHistories = histories.map((h) => (h._id === id ? { ...h, ...data.data.history } : h));
    set({ histories: updatedHistories, loading: false });
    toast.success('History updated successfully');
    // Refresh the list with current pagination
    const currentPagination = get().pagination;
    get().getHistories(undefined, undefined, currentPagination?.page, currentPagination?.limit);
  },

  deleteHistory: async (id: string) => {
    set({ loading: true, error: null });
    const { data, error } = await DeleteHistory<DeleteHistoryResponse>(id);

    if (error || !data.status) {
      set({ loading: false, error: (error as string) || 'Failed to delete history' });
      return;
    }

    const histories = get().histories.filter((history) => history._id !== id);
    set({ histories, loading: false });
    toast.success('History deleted successfully');
    // Refresh the list with current pagination
    const currentPagination = get().pagination;
    get().getHistories(undefined, undefined, currentPagination?.page, currentPagination?.limit);
  },
}));

export type { CreateHistoryI, UpdateHistoryI };
